

/**
 * Shared wizard draft shapes + serialization shared by `NodeFactory` and
 * the canvas palette Save / Factory hydrate flow.
 */

import { normalizePortTypes } from "../../constants/supportedPortTypes";
import type { WidgetDef } from "../../utils/widgets/widgetModel";

export type Category = "data" | "computation" | "vis_grammar" | "vis_simple" | "flow";
export type Engine = "python" | "javascript";
/** Wizard / manifest editor mode (maps to manifest `editor`). */
export type Editor = "code" | "widgets" | "grammar" | "none";

export interface PortDraft {
  id: string;
  /**
   * One entry per declared type.
   *
   * Was a comma-separated STRING, which had to be split on the way out and
   * joined on the way in. Both halves were lossy and silent: a typo survived
   * the editor and was dropped by ``asSupportedTypes``, so a port quietly lost
   * a type (#219). Readers tolerate the old string via ``normalizePortTypes``.
   */
  types: string[];
  cardinality: string;
}

/** Mirrors manifest ``lineage`` (backend ``PackageLineage``). */
export interface PackageLineageCoordDraft {
  packageId: string;
  major: number;
}

export interface PackageLineageDraft {
  forkedFrom: PackageLineageCoordDraft;
  root: PackageLineageCoordDraft;
}

export interface TemplateDraft {
  id: string;
  label: string;
  category: Category;
  engine: Engine;
  editor: Editor;
  description: string;
  hasCode: boolean;
  hasWidgets: boolean;
  hasGrammar: boolean;
  inputPorts: PortDraft[];
  outputPorts: PortDraft[];
  /** Manifest fields surfaced from `curio.builtin@1` behavior/icon registries. Optional for new kinds. */
  behavior?: string;
  iconRef?: string;
  paletteOrder?: number;
  /** Package-relative source filename, e.g. "uhvi-load.py" or "chart.vl.json". Empty when the kind ships no starter. */
  sourceFilename: string;
  /** Full source body, written to `sources/<sourceFilename>` on build. */
  sourceCode: string;
  /** #662: the widgets the source references, written to the template's `widgets`. */
  widgets?: WidgetDef[];
  /** The template's `loadsDataset`: a node of it loads the dataset it names. */
  loadsDataset?: boolean;
  /** The template's `opensOnControls`: a node of it shows only its controls
   *  on its Widgets tab, and opens there. */
  opensOnControls?: boolean;
}

export interface Draft {
  packageId: string;
  major: number;
  version: string;
  name: string;
  publisher: string;
  description: string;
  license: string;
  curioRuntime: string;
  permissions: string[];
  pythonDeps: { id: string; pkg: string; range: string }[];
  jsDeps: { id: string; pkg: string; range: string }[];
  packageDeps: { id: string; pkg: string; range: string }[];
  templates: TemplateDraft[];
  /** Fork provenance when saving from the palette editor against an installed package. */
  lineage: PackageLineageDraft | null;
  /** Manifest ``createdAt`` (ISO instant). Omit to let backend stamp UTC on build/install. */
  createdAt?: string;
  readme: string;
  licenseText: string;
}



// Keep byte-identical to `_STARTER_CODE_SENTINEL` in
// `utk_curio/backend/app/packages/builder/factory.py` — the backend matches this exact
// string to detect "user did not edit this template, keep on-disk source."
export const STARTER_CODE =
  "# `input_0` holds what the edge on input circle 0 delivers (input_1, input_2,\n" +
  "# ... for more circles). Return the value to send downstream.\n" +
  "return input_0\n";

export function factoryUiMakeId(): string {
  return Math.random().toString(36).slice(2, 10);
}

export function makeTemplate(templateId: string = "demo-template"): TemplateDraft {
  return {
    id: templateId,
    label: "New template",
    category: "computation",
    engine: "python",
    editor: "code",
    description: "",
    hasCode: true,
    hasWidgets: false,
    hasGrammar: false,
    inputPorts: [],
    outputPorts: [{ id: factoryUiMakeId(), types: ["JSON"], cardinality: "1" }],
    behavior: "code",
    sourceFilename: `${templateId}.py`,
    sourceCode: STARTER_CODE,
  };
}

export function makeDraft(): Draft {
  return {
    packageId: "",
    major: 1,
    version: "0.1.0",
    name: "",
    publisher: "",
    description: "",
    license: "MIT",
    curioRuntime: ">=0.5.0",
    permissions: [],
    pythonDeps: [],
    jsDeps: [],
    packageDeps: [],
    templates: [makeTemplate()],
    lineage: null,
    readme: "",
    licenseText: "",
  };
}

export function depsToMap(entries: { pkg: string; range: string }[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const e of entries) {
    if (!e.pkg.trim()) continue;
    out[e.pkg.trim()] = e.range.trim() || "*";
  }
  return out;
}

export function toApiPayload(d: Draft): {
  manifest: Record<string, unknown>;
  sources: Record<string, { filename: string; code: string }>;
  readme: string;
  license_text: string;
} {
  const manifest: Record<string, unknown> = {
    id: d.packageId,
    version: d.version,
    name: d.name,
    publisher: d.publisher,
    description: d.description,
    license: d.license || null,
    compatibility: {
      curioRuntime: d.curioRuntime,
      major: d.major,
    },
    permissions: d.permissions,
    dependencies: {
      packages: depsToMap(d.packageDeps),
      python: depsToMap(d.pythonDeps),
      js: depsToMap(d.jsDeps),
    },
    // Wire format: emit ``templates`` (the renamed manifest field). The backend
    // loader still accepts the legacy ``kinds`` key for older third-party drafts.
    templates: d.templates.map((k) => {
      const entry: Record<string, unknown> = {
        id: k.id,
        label: k.label,
        category: k.category,
        engine: k.engine,
        editor: k.editor,
        description: k.description,
        hasCode: k.hasCode,
        hasWidgets: k.hasWidgets,
        hasGrammar: k.hasGrammar,
        inputPorts: k.inputPorts.map((p) => ({
          types: normalizePortTypes(p.types),
          cardinality: p.cardinality,
        })),
        outputPorts: k.outputPorts.map((p) => ({
          types: normalizePortTypes(p.types),
          cardinality: p.cardinality,
        })),
      };
      if (k.behavior) entry.behavior = k.behavior;
      if (k.iconRef) entry.iconRef = k.iconRef;
      if (typeof k.paletteOrder === "number") entry.paletteOrder = k.paletteOrder;
      if (k.sourceFilename) entry.source = `sources/${k.sourceFilename}`;
      if (k.widgets && k.widgets.length > 0) entry.widgets = k.widgets;
      if (k.loadsDataset) entry.loadsDataset = true;
      if (k.opensOnControls) entry.opensOnControls = true;
      return entry;
    }),
  };

  if (typeof d.createdAt === "string" && d.createdAt.trim()) {
    manifest.createdAt = d.createdAt.trim();
  }

  if (d.lineage) {
    manifest.lineage = {
      forkedFrom: {
        packageId: d.lineage.forkedFrom.packageId,
        major: d.lineage.forkedFrom.major,
      },
      root: {
        packageId: d.lineage.root.packageId,
        major: d.lineage.root.major,
      },
    };
  }

  const sources: Record<string, { filename: string; code: string }> = {};
  for (const k of d.templates) {
    if (k.sourceFilename) {
      sources[k.id] = { filename: k.sourceFilename, code: k.sourceCode };
    }
  }
  return {
    manifest,
    sources,
    readme: d.readme,
    license_text: d.licenseText,
  };
}
